import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { studentsAPI, countryEducationAPI } from '../services/api';

// Document tracker V2: staff record which country/education system the
// student's diploma comes from; the matching Country Guide entry is shown
// beside the Denklik items. Reference only — it never changes the checklist.
const panelStyle = {
  padding: '14px 16px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  marginTop: 12,
};
const headingStyle = { color: '#9aa4c4', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 };
const labelStyle = { fontSize: 11, color: '#9aa4c4', display: 'block', marginTop: 8, marginBottom: 4 };
const selectStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.05)', color: '#eee', fontSize: 12, boxSizing: 'border-box',
};
const mutedStyle = { color: '#9aa4c4', fontSize: 12 };
const NOT_IN_GUIDE = '__not_in_guide__';

const DenklikProfilePanel = ({ studentId }) => {
  const [profile, setProfile] = useState(null);
  const [systems, setSystems] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!studentId) return;
    try {
      const [p, s] = await Promise.all([studentsAPI.getDenklikProfile(studentId), countryEducationAPI.list()]);
      setProfile(p.data);
      setNote(p.data.schooling_note || '');
      setSystems(Array.isArray(s.data) ? s.data : (s.data.results || []));
    } catch (err) {
      setError('Could not load the Denklik schooling country.');
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const res = await studentsAPI.updateDenklikProfile(studentId, patch);
      setProfile(res.data);
      setNote(res.data.schooling_note || '');
    } catch (err) {
      setError('Could not save the schooling country.');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div style={panelStyle}>
        <div style={headingStyle}>DENKLIK — SCHOOLING COUNTRY</div>
        {error ? <p style={{ color: '#f5576c', fontSize: 12 }}>{error}</p> : <p style={mutedStyle}>Loading…</p>}
      </div>
    );
  }

  const selectValue = profile.schooling_not_in_guide ? NOT_IN_GUIDE : (profile.schooling_system || '');
  const onSelect = (value) => {
    if (value === NOT_IN_GUIDE) save({ schooling_not_in_guide: true });
    else if (value === '') save({ schooling_system: null, schooling_not_in_guide: false, schooling_note: '' });
    else save({ schooling_system: Number(value) });
  };
  const detail = profile.schooling_system_detail;

  return (
    <div style={panelStyle}>
      <div style={headingStyle}>DENKLIK — SCHOOLING COUNTRY</div>
      {error && <div style={{ color: '#f5576c', fontSize: 12, marginBottom: 8 }}>{error}</div>}

      {!selectValue && (
        <p style={{ ...mutedStyle, marginBottom: 4 }}>
          Set where the student&apos;s diploma comes from, to see that country&apos;s certificate names and caveats here.
          {profile.suggested_system && (
            <>
              {' '}Suggested from home country:{' '}
              <button type="button" disabled={saving} onClick={() => save({ schooling_system: profile.suggested_system.id })}
                style={{ background: 'none', border: 'none', color: '#7aa2ff', cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' }}>
                use {profile.suggested_system.label}
              </button>
            </>
          )}
        </p>
      )}

      <label style={labelStyle}>Diploma issued in</label>
      <select style={selectStyle} value={selectValue} disabled={saving} onChange={(e) => onSelect(e.target.value)}>
        <option value="">Not set</option>
        {systems.map((s) => (
          <option key={s.id} value={s.id}>
            {s.country_name_en}{s.sub_system_label ? ` — ${s.sub_system_label}` : ''}
          </option>
        ))}
        <option value={NOT_IN_GUIDE}>Other / not in guide</option>
      </select>

      {profile.schooling_not_in_guide && (
        <>
          <label style={labelStyle}>Which country / system?</label>
          <input style={selectStyle} value={note} maxLength={300} disabled={saving}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => note !== (profile.schooling_note || '') && save({ schooling_note: note })} />
          <p style={{ ...mutedStyle, marginTop: 8 }}>No guide entry — check the MEB directly.</p>
        </>
      )}

      {detail && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#ddd', lineHeight: 1.5 }}>
          <div style={{ color: '#fff', fontWeight: 600 }}>
            {detail.country_name_en}{detail.sub_system_label ? ` — ${detail.sub_system_label}` : ''}
            {' '}<Link to="/country-guide" style={{ color: '#7aa2ff', fontWeight: 400 }}>Full guide entry</Link>
          </div>
          {detail.certificate_names && detail.certificate_names.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <span style={mutedStyle}>Expected certificates</span>
              <ul style={{ margin: '2px 0 0 18px', padding: 0 }}>
                {detail.certificate_names.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
          {detail.exam_board && <div style={{ marginTop: 6 }}><span style={mutedStyle}>Exam board:</span> {detail.exam_board}</div>}
          {detail.special_notes && (
            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.35)' }}>
              <strong>Caveats:</strong> {detail.special_notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DenklikProfilePanel;
